/**
 * Tela /casos/:id/workspace — workspace pericial (EW.F3.T09 + E8.F5.T06). // ux-neutrality:allow doc-comment
 *
 * 3 colunas (light-workspace.png): // ux-neutrality:allow doc-comment
 *  - esquerda: checklist "Conteúdo do laudo" (E36.F4) + PatternCards // ux-neutrality:allow doc-comment
 *  - meio: ContestedCanvasTools + LRMeter (quando completed) // ux-neutrality:allow doc-comment
 *  - direita: ChatPanel + CriteriaPanel // ux-neutrality:allow doc-comment
 *
 * E8.F5.T06: barra de tabs por contestada (quando K ≥ 2) acima do grid. // ux-neutrality:allow doc-comment
 * Cada tab seleciona qual análise é renderizada nas 3 colunas. // ux-neutrality:allow doc-comment
 *
 * Polling de status a cada 2s enquanto qualquer análise estiver em // ux-neutrality:allow doc-comment
 * {pending, running}. 422 CASE_NOT_READY redireciona para /casos/:id/wizard. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkspaceSkeleton } from '@/components/skeletons';
import { MonoData } from '@/components/ui/MonoData';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { getCase, type CaseRecordData } from '@/lib/cases';
import { WorkspaceHeader } from './WorkspaceHeader';
import { WorkspaceRightTabs } from './WorkspaceRightTabs';
import {
  AgentSummaryCard,
  ContestedCanvasTools,
  LaudoContentChecklist,
  LRMeter,
  PatternCard,
  PatternDegradationBanner,
  ProportionWarningBanner,
  InstrumentPanels,
  ReferenceSetInstabilityBanner,
  WorkspaceResizableLayout,
} from '@/components/Workspace';
import type { CaseAnalysisData, ProportionWarningData } from '@/lib/case_workspace';
import { classifyProviderFailure } from '@/lib/case_source_documents';
import type { SignatureRecordData } from '@/lib/case_signatures';
import { useLaudoAttachments } from '@/components/Workspace/useLaudoAttachments';
import { ContestedTabs } from './WorkspaceContestedTabs';
import { useWorkspaceData } from './useWorkspaceData';

/**
 * E70.F2: a análise que falhou por saldo/chave do provedor diz isso — antes
 * toda falha virava "recarregue a página", e o saldo esgotado da conta passava
 * por defeito do produto.
 */
function analysisFailedHint(errorReason: string | null): string {
  const recusa = classifyProviderFailure(errorReason);
  if (recusa === 'provider_credit') return ptBR.workspaceCaso.analysisFailedProviderCredit;
  if (recusa === 'provider_auth') return ptBR.workspaceCaso.analysisFailedProviderAuth;
  return ptBR.workspaceCaso.analysisRetry;
}

export function WorkspaceCasoPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useApiClient();

  const {
    analyses,
    activeContestedId,
    setActiveContestedId,
    activeAnalysis,
    signatures,
    messages,
    proportionWarning,
    loadError,
    chatError,
    sending,
    sendChat,
    adjusting,
    adjustError,
    adjustLr,
    clearLr,
    setSystemMarkers,
  } = useWorkspaceData(client, caseId, navigate);

  const [pendingCriterion, setPendingCriterion] = useState<string | null>(null);
  const [selectedPatternId, setSelectedPatternId] = useState<string | null>(null);
  const [caseRecord, setCaseRecord] = useState<CaseRecordData | null>(null);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    void getCase(client, caseId)
      .then((rec) => {
        if (!cancelled) setCaseRecord(rec);
      })
      .catch(() => {
        // Silencioso: o header cai para fallback com o caseId; não bloqueia o workspace.
      });
    return () => {
      cancelled = true;
    };
  }, [client, caseId]);

  const selectedPattern = useMemo(() => {
    const id = selectedPatternId ?? signatures.find((s) => s.kind === 'standard')?.id;
    return signatures.find((s) => s.id === id) ?? null;
  }, [selectedPatternId, signatures]);

  // E8.F5: a contestada da tab ativa (não mais "primeira contestada do caso").
  const contested = useMemo(() => {
    if (!activeAnalysis) return null;
    return signatures.find((s) => s.id === activeAnalysis.contested_signature_id) ?? null;
  }, [activeAnalysis, signatures]);

  const patternResults = useMemo(() => {
    const map = new Map<string, CaseAnalysisData['patterns'][number]>();
    if (!activeAnalysis) return map;
    for (const p of activeAnalysis.patterns) map.set(p.signature_id, p);
    return map;
  }, [activeAnalysis]);

  const handleAskAgent = useCallback((criterionId: string) => {
    setPendingCriterion(criterionId);
  }, []);

  const handleSend = useCallback(
    (content: string, criterionId: string | null) => {
      void sendChat(content, criterionId).then(() => setPendingCriterion(null));
    },
    [sendChat],
  );

  // E33.F2/ADR-0044: ajuste manual de LR pelo perito na mesa. O erro já é
  // exposto via `adjustError` (banner) — aqui só evitamos a rejeição não tratada.
  const handleAdjustCriterion = useCallback(
    (criterionKey: string, value: number): Promise<void> => {
      if (!activeAnalysis) return Promise.resolve();
      return adjustLr(activeAnalysis.id, {
        scope: 'criterion',
        criterion_key: criterionKey,
        value,
      }).catch(() => undefined);
    },
    [activeAnalysis, adjustLr],
  );
  const handleClearCriterion = useCallback(
    (criterionKey: string): Promise<void> => {
      if (!activeAnalysis) return Promise.resolve();
      return clearLr(activeAnalysis.id, 'criterion', criterionKey).catch(() => undefined);
    },
    [activeAnalysis, clearLr],
  );
  const handleAdjustComposite = useCallback(
    (value: number): Promise<void> => {
      if (!activeAnalysis) return Promise.resolve();
      return adjustLr(activeAnalysis.id, { scope: 'composite', value }).catch(() => undefined);
    },
    [activeAnalysis, adjustLr],
  );
  const handleClearComposite = useCallback((): Promise<void> => {
    if (!activeAnalysis) return Promise.resolve();
    return clearLr(activeAnalysis.id, 'composite').catch(() => undefined);
  }, [activeAnalysis, clearLr]);
  // E38.F2: alterna a visibilidade das marcações do sistema (persistida por
  // análise; oculto = fora do laudo). A reversão otimista no hook é o feedback.
  const handleToggleSystemMarkers = useCallback((): void => {
    if (!activeAnalysis) return;
    void setSystemMarkers(activeAnalysis.id, !(activeAnalysis.show_system_markers ?? false));
  }, [activeAnalysis, setSystemMarkers]);

  if (loadError && !activeAnalysis) {
    return (
      <article role="alert" data-testid="workspace-load-error">
        <p>{loadError}</p>
      </article>
    );
  }
  if (!activeAnalysis) {
    return (
      <div data-testid="workspace-loading">
        <span className="sr-only" role="status">
          {ptBR.workspaceCaso.loading}
        </span>
        <WorkspaceSkeleton />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      caseId={caseId}
      caseRecord={caseRecord}
      analyses={analyses}
      activeContestedId={activeContestedId}
      onSelectContested={setActiveContestedId}
      analysis={activeAnalysis}
      signatures={signatures}
      messages={messages}
      proportionWarning={proportionWarning}
      sending={sending}
      chatError={chatError}
      pendingCriterion={pendingCriterion}
      selectedPattern={selectedPattern}
      contested={contested}
      patternResults={patternResults}
      onSelectPattern={setSelectedPatternId}
      onAskAgent={handleAskAgent}
      onSend={handleSend}
      adjusting={adjusting}
      adjustError={adjustError}
      onAdjustCriterion={handleAdjustCriterion}
      onClearCriterion={handleClearCriterion}
      onAdjustComposite={handleAdjustComposite}
      onClearComposite={handleClearComposite}
      onToggleSystemMarkers={handleToggleSystemMarkers}
    />
  );
}

interface WorkspaceLayoutProps {
  caseId: string;
  caseRecord: CaseRecordData | null;
  analyses: CaseAnalysisData[];
  activeContestedId: string | null;
  onSelectContested: (id: string) => void;
  analysis: CaseAnalysisData;
  signatures: SignatureRecordData[];
  messages: ReturnType<typeof useWorkspaceData>['messages'];
  proportionWarning: ProportionWarningData | null;
  sending: boolean;
  chatError: string | null;
  pendingCriterion: string | null;
  selectedPattern: SignatureRecordData | null;
  contested: SignatureRecordData | null;
  patternResults: Map<string, CaseAnalysisData['patterns'][number]>;
  onSelectPattern: (id: string) => void;
  onAskAgent: (criterionId: string) => void;
  onSend: (content: string, criterionId: string | null) => void;
  // E33.F2/ADR-0044: ajuste manual de LR pelo perito na mesa.
  adjusting: boolean;
  adjustError: string | null;
  onAdjustCriterion: (criterionKey: string, value: number) => Promise<void>;
  onClearCriterion: (criterionKey: string) => Promise<void>;
  onAdjustComposite: (value: number) => Promise<void>;
  onClearComposite: () => Promise<void>;
  // E38.F2: alterna a visibilidade persistida das marcações do sistema.
  onToggleSystemMarkers: () => void;
}

function WorkspaceLayout(props: WorkspaceLayoutProps): JSX.Element {
  const {
    caseId,
    caseRecord,
    analyses,
    activeContestedId,
    onSelectContested,
    analysis,
    signatures,
    messages,
    proportionWarning,
    sending,
    chatError,
    pendingCriterion,
    selectedPattern,
    contested,
    patternResults,
    onSelectPattern,
    onAskAgent,
    onSend,
    adjusting,
    adjustError,
    onAdjustCriterion,
    onClearCriterion,
    onAdjustComposite,
    onClearComposite,
    onToggleSystemMarkers,
  } = props;
  const standards = signatures.filter((s) => s.kind === 'standard');
  // E36.F4: capturas da mesa → conteúdo do laudo. O hook vive aqui porque o
  // botão de capturar (centro) e o checklist (esquerda) compartilham o estado.
  const laudoAttachments = useLaudoAttachments(caseId, analysis.id);

  // E37.F3.T03: modo foco — recolhe as colunas laterais para análise imersiva.
  // O estado vive aqui (o botão está na toolbar do centro e o recolhimento é do
  // layout); efêmero/apresentação-only. Esc sai do foco.
  const [focusMode, setFocusMode] = useState(false);
  useEffect(() => {
    if (!focusMode) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setFocusMode(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusMode]);

  return (
    <>
      <WorkspaceHeader caseId={caseId} caseRecord={caseRecord} analysis={analysis} />
      <article className="workspace-page" data-testid="workspace-page">
        <div className="mb-4 flex flex-col gap-3">
          <ContestedTabs
            analyses={analyses}
            signatures={signatures}
            activeContestedId={activeContestedId}
            onSelect={onSelectContested}
          />
          {/* key={caseId}: a identidade do aviso é o caso. Remontar ao trocar de // ux-neutrality:allow doc-comment
              caso reseta o dismiss local (F-E15.F4-01), preservando-o dentro do // ux-neutrality:allow doc-comment
              mesmo caso entre refreshes/polling. */}
          <ProportionWarningBanner key={caseId} warning={proportionWarning} />
          {/* E16.F2: a instabilidade é POR ANÁLISE (cada contestada tem seu // ux-neutrality:allow doc-comment
              conjunto de comparações), então a identidade do aviso é a análise — // ux-neutrality:allow doc-comment
              key={analysis.id} reseta o dismiss ao trocar de contestada e o // ux-neutrality:allow doc-comment
              preserva entre refreshes da mesma (mesma lição da F-E15.F4-01). */}
          <ReferenceSetInstabilityBanner
            key={analysis.id}
            instability={analysis.reference_set_instability}
          />
          {/* E25.F3/ADR-0034 F: aviso por análise de padrão com qualidade de // ux-neutrality:allow doc-comment
              captura insuficiente — key separada da instabilidade (avisos // ux-neutrality:allow doc-comment
              independentes, dismiss independente; mesma lição F-E15.F4-01). */}
          <PatternDegradationBanner
            key={`degradation-${analysis.id}`}
            degradation={analysis.pattern_degradation}
          />
          {/* E24.F2.T02: o aviso textual de análise em andamento migrou para o
              chip com Spinner no WorkspaceHeader (role=status lá); manter os
              dois duplicaria o anúncio para leitores de tela. */}
          {analysis.status === 'failed' && (
            <div role="alert" className="workspace-failed" data-testid="workspace-failed">
              <p>{ptBR.workspaceCaso.analysisFailed}</p>
              <p>{analysisFailedHint(analysis.error_reason)}</p>
            </div>
          )}
        </div>

        <WorkspaceResizableLayout
          focusMode={focusMode}
          left={
            <>
              {/* E36.F4: checklist do conteúdo do laudo no lugar do stepper
                  decorativo — mesma fonte de estado do botão de capturar. */}
              <LaudoContentChecklist
                caseId={caseId}
                analysisId={analysis.id}
                attachments={laudoAttachments.attachments}
                saveError={laudoAttachments.saveError}
                onRename={laudoAttachments.rename}
                onSetIncluded={laudoAttachments.setIncluded}
                onRemove={laudoAttachments.remove}
              />
              {/* Prancha 2B: título da lista com o contador em mono à direita. */}
              <div className="mt-sc-4 flex items-baseline justify-between gap-2">
                <h3>{ptBR.workspaceCaso.patternsTitle}</h3>
                <MonoData variant="label" data-testid="workspace-patterns-count">
                  {ptBR.workspaceCaso.patternsCount.replace('{count}', String(standards.length))}
                </MonoData>
              </div>
              <ul className="pattern-list">
                {standards.map((sig, idx) => (
                  <li key={sig.id}>
                    <PatternCard
                      signature={sig}
                      patternResult={patternResults.get(sig.id) ?? null}
                      caseId={caseId}
                      selected={selectedPattern?.id === sig.id}
                      onSelect={() => onSelectPattern(sig.id)}
                      index={idx}
                    />
                  </li>
                ))}
              </ul>
            </>
          }
          center={
            <>
              {analysis.status === 'completed' && analysis.composite_lr !== null && (
                <>
                  <LRMeter
                    lr={analysis.composite_lr}
                    direction={analysis.composite_lr_direction}
                    supportIndex={analysis.composite_lr_support_index}
                    supportLevel={analysis.composite_lr_support_level}
                    adjustment={analysis.lr_adjustment}
                    busy={adjusting}
                    onAdjust={onAdjustComposite}
                    onClear={onClearComposite}
                  />
                  {adjustError && (
                    <p
                      role="alert"
                      className="lr-adjust-error mt-1 text-caption text-danger-700"
                      data-testid="lr-adjust-error"
                    >
                      {adjustError}
                    </p>
                  )}
                </>
              )}
              {contested && (
                <ContestedCanvasTools
                  caseId={caseId}
                  analysisId={analysis.id}
                  contested={contested}
                  selectedPattern={selectedPattern}
                  localizations={analysis.questioned_localizations}
                  criteriaStatus={analysis.criteria_status}
                  laudoCapture={{
                    busy: laudoAttachments.busy,
                    onCapture: laudoAttachments.capture,
                  }}
                  focusMode={focusMode}
                  onToggleFocus={() => setFocusMode((current) => !current)}
                  showSystemMarkers={analysis.show_system_markers ?? false}
                  onToggleSystemMarkers={onToggleSystemMarkers}
                />
              )}
              {/* E26 (ADR-0036/ADR-0037): instrumentação visual honesta K|Q —
                  topografia estimada + gênese gráfica, colapsadas por padrão. */}
              {contested && (
                <InstrumentPanels
                  caseId={caseId}
                  contested={contested}
                  selectedPattern={selectedPattern}
                />
              )}
              {analysis.status === 'completed' && (
                <AgentSummaryCard
                  criteriaBreakdown={analysis.criteria_breakdown}
                  criteriaStatus={analysis.criteria_status}
                  narrative={analysis.agent_summary}
                />
              )}
            </>
          }
          right={
            <WorkspaceRightTabs
              messages={messages}
              sending={sending}
              chatError={chatError}
              pendingCriterion={pendingCriterion}
              analysis={analysis}
              onAskAgent={onAskAgent}
              onSend={onSend}
              adjusting={adjusting}
              onAdjustCriterion={onAdjustCriterion}
              onClearCriterion={onClearCriterion}
            />
          }
        />
      </article>
    </>
  );
}
